import { getQueryUrl, replaceUrl } from '../helpers/query-string';
import { loader } from '../helpers/loader';
import FunnelFilters from './FunnelFilters/FunnelFilters';
import FunnelCompanies from './FunnelCompanies/FunnelCompanies';
import FunnelPrice from './FunnelPrice/FunnelPrice';
import GeoControl from '../GeoControl/GeoControl';
import Modal from '../UiKit/Elements/Modal/Modal';
import { initToggleChips } from '../UiKit/Elements/ToggleChip/ToggleChip';
import NavigationCityRadius from '../Navigation/NavigationCityRadius/NavigationCityRadius';

const PRICE_KEY = 'price';
const COMPANIES_KEY = 'companies';
const FILTERS_KEY = 'filters';

const resetPage = (prevQuery, nextQuery) => {
  const keys = Object.keys(prevQuery);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (key === 'page') continue;
    if (prevQuery[key] !== nextQuery[key]) {
      return true;
    }
  }
  return false;
};

const updateQueryLinks = (query) => {
  const queryLinks = document.querySelectorAll('[data-query-link]');
  delete query.page;
  delete query.order;
  delete query.building_id;
  [].forEach.call(queryLinks, (link) => {
    const linkType = link.dataset.queryLink;
    let path;

    if (linkType === 'catalog' && window.params.catalogUrl) {
      path = window.params.catalogUrl;
    } else if (linkType === 'map' && window.params.mapUrl) {
      path = window.params.mapUrl;
    } else {
      path = link.getAttribute('href').split('?')[0]; // remove old query params
    }

    link.setAttribute('href', getQueryUrl({ path, params: query }));
  });
};

const getCleanQuery = (query) => {
  const radiusSliderNode = document.querySelector('.NavigationCityRadius[data-realty-type]:not(.hidden) [data-slider-default]');
  const result = { ...query };

  Object.keys(result).forEach((key) => {
    if (result[key] === false) {
      delete result[key];
    }
  });

  if (result.page === 1) {
    delete result.page;
  }

  if (radiusSliderNode) {
    const sliderDefaultValue = Number(radiusSliderNode.dataset.sliderDefault);
    if (result.radius === sliderDefaultValue) {
      delete result.radius;
    }
  }

  if (result.invest) {
    delete result.invest;
  }

  return result;
};

const updateGeoControlCount = (filter) => {
  const sections = [...document.querySelectorAll('[data-geo-section]')];
  const trigger = document.querySelector('[data-uipopup-trigger="local"]');
  if (!filter || !sections.length || !trigger) return;
  const count = filter.count();
  const totalCount = Object.values(count).reduce((acc, value) => acc + value, 0);
  const triggerCounterNode = trigger.querySelector('[data-counter]');

  triggerCounterNode.innerHTML = totalCount > 0 ? totalCount : '';

  sections.forEach((section) => {
    const counterNode = section.querySelector('[data-counter]');
    const key = section.getAttribute('data-geo-section');

    counterNode.innerHTML = count[key] > 0 ? count[key] : '';
  });
};

class Funnel {
  constructor(options = {}) {
    this.filtersNode = document.querySelector(`[data-funnel="${FILTERS_KEY}"]`);
    this.companiesNode = document.querySelector(`[data-funnel="${COMPANIES_KEY}"]`);
    this.companiesTrigger = document.querySelector(`[data-trigger="${COMPANIES_KEY}"]`);
    this.query = window.params.initialQuery;
    this.toggle = this.toggle.bind(this);
    this.switch = this.switch.bind(this);
    this.assign = this.assign.bind(this);
    this.reset = this.reset.bind(this);
    this.options = options;
    this.onResponse = options.onResponse;
    this.onFetch = options.onFetch;
    this.endpoint = options.endpoint;
    this.requestTimeout = null;
    this.modal = new Modal(FILTERS_KEY, {
      onClose: () => {
        if (this.filtersNode.classList.contains('hidden')) {
          this.filtersNode.classList.remove('hidden');
          this.companiesNode.classList.add('hidden');
        }
        setTimeout(() => {
          this.filtersNode.querySelector('[data-funnel-scrollable]').scrollTop = 0;
        }, 400);
      },
      controlledFirstRender: true,
    });

    const navigationCityRadius = new NavigationCityRadius('navigation-city-select',{
      onAssign: this.assign,
      onReset: this.reset,
    });
    const searchBarCityRadius = new NavigationCityRadius('search-bar-city-select',{
      onAssign: this.assign,
      onReset: this.reset,
    });

    this.funnelFilters = new FunnelFilters({
      onToggle: this.toggle,
      onSwitch: this.switch,
      onAssign: this.assign,
      modal: this.modal,
      onReset: (keys) => {
        if (!this.funnelPrice) return;
        this.funnelPrice.reset();
        this.reset({...keys, ...this.funnelPrice.resetKeys});
      },
      onRealtyTypeChange: (params) => {
        this.funnelFilters?.reset();
        this.funnelPrice?.reset();
        if (this.funnelCompanies?.root) {
          this.funnelCompanies.reset();
        }
        navigationCityRadius.updateRealtyType();
        searchBarCityRadius.updateRealtyType();
        this.setQuery(params);
        this.updateFilters(params);
      },
    });

    this.funnelCompanies = new FunnelCompanies({
      onToggle: this.toggle,
      onAssign: this.assign,
      onReset: this.reset,
    });

    this.funnelPrice = new FunnelPrice({
      onAssign: this.assign,
      onSwitch: this.switch,
    });

    this.geoControl = new GeoControl({
      onToggle: this.toggle,
      onAssign: this.assign,
      onReset: this.reset,
      onSelect: options.onSelect,
    });

    updateQueryLinks(this.cleanQuery);
    this.initCompaniesTrigger();
    this.dropdown();
  }

  get cleanQuery() {
    return getCleanQuery(this.query);
  }

  get requestBody() {
    return JSON.stringify({
      lang: window.currentLanguage,
      url: window.params.geoUrl,
      investListing: window.params.investListing,
      query: this.cleanQuery,
    })
  }

  toggle(key, value, fitBounds) {
    const newQuery = { ...this.query };
    if (newQuery[key].indexOf(value) !== -1) {
      newQuery[key] = newQuery[key].filter((v) => value !== v);
    } else {
      newQuery[key] = [...newQuery[key], value];
    }
    this.setQuery(newQuery, { fitBounds });
    this.updateCount();
  }

  switch(key) {
    this.setQuery({ ...this.query, [key]: !this.query[key] });
    this.updateCount();
  }

  assign(key, value, fitBounds, withRequest = true) {
    this.setQuery({ ...this.query, [key]: value }, { fitBounds, withRequest });
    this.updateCount();
  }

  reset(cleared, fitBounds, withRequest = true) {
    this.setQuery({ ...this.query, ...cleared }, { fitBounds, withRequest });
    this.updateCount();
  }

  updateFunnelCount = () => {
    const trigger = document.querySelector(`[data-funnel-trigger="${FILTERS_KEY}"]`);
    const companiesTrigger = document.querySelector(`[data-trigger="${COMPANIES_KEY}"]`);
    if (!trigger || !companiesTrigger) return;
    const counterNode = trigger.querySelector('[data-counter]');
    const companiesCounterNode = companiesTrigger.querySelector('[data-counter]');
    const companiesCount = this.funnelCompanies.count();
    const count = this.funnelFilters.count() + this.funnelPrice.count() + companiesCount;

    companiesCounterNode.innerHTML = companiesCount > 0 ? companiesCount : '';
    counterNode.innerHTML = count > 0 ? count : '';
  };

  updateCount() {
    this.updateFunnelCount();
    updateGeoControlCount(this.geoControl.geoControlLocal);
  }

  setQuery(query, options = {}) {
    const {
      fitBounds,
      withRequest = true,
      triggerReplaceUrl = true
    } = options;
    if (resetPage(this.query, query)) {
      query.page = 1;
    }
    this.query = query;
    clearTimeout(this.requestTimeout);
    this.requestTimeout = setTimeout(() => {
      if (withRequest) {
        this.sendRequest(fitBounds);
      }
      if (triggerReplaceUrl) {
        replaceUrl({ path: window.params.geoUrl, params: this.cleanQuery });
      }
    }, 200);
  }

  dropdown() {
    const trigger = document.querySelector('[data-modal-trigger="filters"]');

    trigger.addEventListener('click', () => this.modal.show());
  }

  sendRequest(fitBounds) {
    loader.show();
    if (this.onFetch) {
      this.onFetch(this.requestBody, fitBounds);
    }
    fetch(this.endpoint, {
      method: 'post',
      headers: {
        'Content-type': 'application/json',
        'X-CSRF-Token': window.csrf,
      },
      credentials: 'same-origin',
      body: this.requestBody,
    })
      .then((res) => res.json())
      .then((json) => {
        const countNodes = document.querySelectorAll('[data-count="buildings"]');
        [].forEach.call(countNodes, (node) => {
          node.textContent = json.buildings_count_with_type;
        });
        this.onResponse(json, fitBounds);
        updateQueryLinks(this.cleanQuery);
        loader.hide();
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  updateFilters(query) {
    fetch('/api/filters', {
      method: 'post',
      headers: {
        'Content-type': 'application/json',
        'X-CSRF-Token': window.csrf,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        lang: window.currentLanguage,
        url: window.params.geoUrl,
        query,
      }),
    })
      .then((res) => res.json())
      .then(({ price, companies, geo }) => {
        const priceNode = document.querySelector(`[data-funnel="${PRICE_KEY}"]`);
        const geoSearchTrigger = document.querySelector('[data-geo-trigger="local"]');
        const geoSearchNode = document.querySelector('[data-geo="local"]');

        if (geoSearchTrigger) {
          geoSearchTrigger.innerHTML = geo.triggerHtml;
          geoSearchNode.innerHTML = geo.dropdownHtml;
          window.params.subwayRoutes = geo.params.subwayRoutes;
          window.params.bank = geo.params.bank;
          this.geoControl = new GeoControl({
            onToggle: this.toggle,
            onAssign: this.assign,
            onReset: this.reset,
            onSelect: this.options.onSelect,
          });
        }

        priceNode.innerHTML = price.html;
        this.funnelPrice = new FunnelPrice({
          onAssign: this.assign,
          onSwitch: this.switch,
        });
        initToggleChips(priceNode);

        if (companies.html) {
          this.companiesTrigger.classList.remove('hidden');
          this.companiesNode.innerHTML = companies.html;
          window.params.hasReadyCompanies = companies.params.hasReadyCompanies;
          window.params.companiesParams = companies.params.companiesParams;
          this.funnelCompanies = new FunnelCompanies({
            onToggle: this.toggle,
            onAssign: this.assign,
            onReset: this.reset,
          });
        } else {
          this.companiesTrigger.classList.add('hidden');
        }
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  initCompaniesTrigger() {
    this.companiesTrigger.addEventListener('click', () => {
      this.filtersNode.classList.add('hidden');
      this.companiesNode.classList.remove('hidden');
    });
  }
}

export default Funnel;
